import React, { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline, Util } from '@finley-technologies/finley-types';

import { lockMeshRun } from './lockMeshRun';
import { NeededInput } from './neededInput';
import { LockedMeshRun, UploadDrawerContent } from './UploadDrawerContent';
import {
  UploadDrawerMessageContent,
  UploadDrawerShell,
  UploadDrawerSkeletonContent,
} from './UploadDrawerShell';
import { useLatestMeshDocumentMetadata } from 'packages/dataPipeline/hooks/useLatestMeshDocumentMetadata';
import {
  useGetImplementationPipelineRunsQuery,
  useGetPipelineRunInputsQuery,
} from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import {
  RDFV,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

export const UploadDrawerExistingRun = ({
  organizationId,
  onClose,
  selectedMeshRun,
}: {
  organizationId: string;
  onClose: () => void;
  selectedMeshRun: SelectedMeshRun;
}) => {
  return (
    <UploadDrawerShell onClose={onClose}>
      <UploadDrawerContent
        organizationId={organizationId}
        onClose={onClose}
        meshDocuments={[selectedMeshRun.meshRunParams.meshDocumentMetadata]}
        lockedMeshRun={selectedMeshRun}
        neededInput={null}
      />
    </UploadDrawerShell>
  );
};

// start new run for given credit facility and (optionally reporting date)
const UploadDrawerForCreditFacility = ({
  organizationId,
  latestMeshDocuments,
  creditFacilityId,
  reportingDate,
  onClose,
}: {
  organizationId: string;
  latestMeshDocuments: DataPipeline.MeshDocumentMetadata[];
  creditFacilityId: string;
  reportingDate?: Util.FullDateStr;
  onClose: () => void;
}): React.ReactElement => {
  // find meshDocumentId for the creditFacilityId
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacility = facilities.find((cf) => cf.id === creditFacilityId);

  finleyAssert(
    creditFacility,
    `UploadDrawerNewRunForCFAndDate: Credit facility not found for [id:${creditFacilityId}]`,
  );

  const meshDocumentMetadata = latestMeshDocuments.find((latestDocument) =>
    latestDocument.cfSlugs.includes(creditFacility.slug),
  );

  // Query for latest file version, executed only when mesh document data is ready
  const {
    data,
    loading: pipelinesLoading,
    error: pipelinesError,
  } = useGetImplementationPipelineRunsQuery({
    skip: !meshDocumentMetadata || !reportingDate,
    variables: {
      implementationId: meshDocumentMetadata?.implementationId || '',
      startDate: reportingDate,
      endDate: reportingDate,
    },
    fetchPolicy: 'network-only',
  });

  // the next version of the date: 1 when it has no runs yet.
  const rdfv: RDFV | null = useMemo(() => {
    if (!reportingDate || !data) {
      return null;
    }
    const latestFileVersion = Math.max(
      0,
      ...data.getPipelineRuns
        .filter((run) => run.reportingDate === reportingDate)
        .map((run) => run.fileVersion),
    );
    return { reportingDate, fileVersion: latestFileVersion + 1 };
  }, [data, reportingDate]);

  if (!meshDocumentMetadata) {
    return (
      <UploadDrawerShell onClose={onClose}>
        <UploadDrawerMessageContent
          onClose={onClose}
          message={`${creditFacility.name} has no data pipeline to upload to.`}
        />
      </UploadDrawerShell>
    );
  }
  if (pipelinesError) {
    return (
      <UploadDrawerShell onClose={onClose}>
        <UploadDrawerMessageContent
          onClose={onClose}
          message={`Could not load the versions of ${reportingDate}: ${pipelinesError.message}`}
        />
      </UploadDrawerShell>
    );
  }

  const loading = pipelinesLoading || (!!reportingDate && !rdfv);

  return (
    <UploadDrawerShell onClose={onClose}>
      {loading ? (
        <UploadDrawerSkeletonContent onClose={onClose} />
      ) : (
        <UploadDrawerContent
          organizationId={organizationId}
          onClose={onClose}
          meshDocuments={[meshDocumentMetadata]}
          lockedMeshRun={
            rdfv && {
              meshRunId: null,
              meshRunParams: { meshDocumentMetadata, rdfv },
            }
          }
          neededInput={null}
        />
      )}
    </UploadDrawerShell>
  );
};

/**
 * Opens the drawer on an implementation's run of a reportingDate (see
 * lockMeshRun). For callers that know the implementation but no credit
 * facility, e.g. the backup-servicing portal.
 */
export const UploadDrawerForImplementation = ({
  organizationId,
  implementationId,
  reportingDate,
  neededInput,
  onClose,
}: {
  organizationId: string;
  implementationId: string;
  reportingDate: Util.FullDateStr;
  // the input the caller needs: the drawer marks it among the others
  neededInput: NeededInput | null;
  onClose: () => void;
}): React.ReactElement => {
  const {
    latestMeshDocumentMetadata,
    loading: meshDocumentsLoading,
    error: meshDocumentsError,
  } = useLatestMeshDocumentMetadata({
    organizationId,
    forceNetworkFetch: true,
  });
  const meshDocuments = latestMeshDocumentMetadata.filter(
    (meshDocument) => meshDocument.implementationId === implementationId,
  );

  // no slugs: only the runs themselves are needed, not what each received
  const {
    data,
    loading: runsLoading,
    error: runsError,
  } = useGetPipelineRunInputsQuery({
    variables: { implementationId, reportingDate, artifactSlugs: [] },
    fetchPolicy: 'network-only',
  });

  const [meshDocumentMetadata] = meshDocuments;
  const lockedMeshRun: LockedMeshRun | null = useMemo(
    () =>
      data && meshDocuments.length === 1
        ? lockMeshRun({
            runs: data.getPipelineRunInputs,
            meshDocumentMetadata,
            reportingDate,
          })
        : null,
    [data, meshDocuments.length, meshDocumentMetadata, reportingDate],
  );

  const error = meshDocumentsError ?? runsError ?? null;
  const message = ((): string | null => {
    if (error) {
      return `Could not load the data pipeline of ${reportingDate}: ${error.message}`;
    }
    if (meshDocumentsLoading) {
      return null;
    }
    if (meshDocuments.length === 0) {
      return 'This deal has no data pipeline to upload to.';
    }
    if (meshDocuments.length > 1) {
      return 'This deal has more than one data pipeline. Upload from Data Pipelines.';
    }
    return null;
  })();

  if (message) {
    return (
      <UploadDrawerShell onClose={onClose}>
        <UploadDrawerMessageContent onClose={onClose} message={message} />
      </UploadDrawerShell>
    );
  }

  return (
    <UploadDrawerShell onClose={onClose}>
      {runsLoading || !lockedMeshRun ? (
        <UploadDrawerSkeletonContent onClose={onClose} />
      ) : (
        <UploadDrawerContent
          organizationId={organizationId}
          onClose={onClose}
          meshDocuments={meshDocuments}
          lockedMeshRun={lockedMeshRun}
          neededInput={neededInput}
        />
      )}
    </UploadDrawerShell>
  );
};

export const UploadDrawer = ({
  organizationId,
  creditFacilityId,
  reportingDate,
  onClose,
}: {
  organizationId: string;
  creditFacilityId?: string;
  reportingDate?: Util.FullDateStr;
  onClose: () => void;
}) => {
  const {
    latestMeshDocumentMetadata: latestMeshDocuments,
    loading,
  } = useLatestMeshDocumentMetadata({
    organizationId,
    // when loading drawer force network fetch to get latest mesh documents
    forceNetworkFetch: true,
  });

  if (loading) {
    return (
      <UploadDrawerShell onClose={onClose}>
        <UploadDrawerSkeletonContent onClose={onClose} />
      </UploadDrawerShell>
    );
  }

  if (creditFacilityId) {
    return (
      <UploadDrawerForCreditFacility
        organizationId={organizationId}
        latestMeshDocuments={latestMeshDocuments}
        creditFacilityId={creditFacilityId}
        reportingDate={reportingDate}
        onClose={onClose}
      />
    );
  }

  return (
    <UploadDrawerShell onClose={onClose}>
      <UploadDrawerContent
        organizationId={organizationId}
        onClose={onClose}
        meshDocuments={latestMeshDocuments}
        lockedMeshRun={null}
        neededInput={null}
      />
    </UploadDrawerShell>
  );
};
